import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { Component, ElementRef, Inject, ViewChild, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, startWith, map } from 'rxjs';
import { FileUploadService } from 'src/app/shared/service/disbursment/file-upload.service';

interface PaymentInstructionData {
  to?: string[];
  cc?: string[];
  vesselName?: string;
  voyage?: string;
  type?: string;
  port?: string;
  purpose?: string;
  portAgent?: string;
  port_agent_office_address?: string;
  portAgentOfficeAddress?: string;
  pdaAmount?: string;
  advancePay?: string;
  due?: string;
  advancePercentage: number;
  disbursement_id: string;
  pmt_curr_to: string;
  bankDetails?: {
    account_holder_name?: string;
    beneficiary_acc_holder_name?: string;
    account_no?: string;
    current_account_number?: string;
    swift_code?: string;
    bank_name?: string;
    branch_address?: string;
    correspondent_account_number?: string;
    ifsc_code?: string;
    bik_code?: string;
    bic_code?: string;
    inn_code?: string;
    iban_number?: string;
  };
  presignedUrl?: string;
  pdf_name?: any;
  disbursement_seq?: any;
  vessel_imo: any;
}

@Component({
  selector: 'app-payment-instruction-dialog',
  templateUrl: './payment-instruction-dialog.component.html',
  styleUrls: ['./payment-instruction-dialog.component.scss'],
})
export class PaymentInstructionDialogComponent implements OnInit {
  reportPdfUrl: string | null = null;
  reportPdfName: string | null = null;
  isReportLoading = false;
  toEmailList: string[] = [];
  ccEmailList: string[] = [];
  selectedToEmails: string[] = [];

  portAgentOfficeAddress: string | null = null;

  bankDetailsData: {
    account_holder_name?: string;
    account_no?: string;
    swift_code?: string;
    bank_name?: string;
    branch_address?: string;
    correspondent_account_number?: string;
    ifsc_code?: string;
    bik_code?: string;
    bic_code?: string;
    inn_code?: string;
    iban_number?: string;
  } | null = null;

  selectedCcEmails: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  subject: string = '';
  bottomTextContent: string =
    '<b>Charterers account items / World scale items</b> - No such cost in invoice<br><b>Owners account items rejected</b> - No owner cost item in invoice.';

  to = new FormControl('', [Validators.email]);
  cc = new FormControl('');
  greetingText: string = '';
  tableData: any[] = [];
  signatureData = sessionStorage.getItem('signature');
  defaultSignature = `Many Thanks & Best Regards,

✉ disbursement@merakishippingservices.com

Meraki Shipping and Technical Management

Remarks:- Please mention the ship name in the email subject for easy identification.

Kindly send your inquiries by email for a prompt response.`;
  signature = new FormControl(this.signatureData || this.defaultSignature);
  update_signature = new FormControl(false);

  @ViewChild('topTextDiv') topTextDiv!: ElementRef;
  @ViewChild('bottomTextDiv') bottomTextDiv!: ElementRef;

  filteredToOptions!: Observable<string[]>;
  filteredCcOptions!: Observable<string[]>;

  constructor(
    private dialogRef: MatDialogRef<PaymentInstructionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentInstructionData,
    private fileUploadService: FileUploadService,
  ) {
    this.toEmailList = data?.to || [];
    this.ccEmailList = data?.cc || [];
    const vesselName = data?.vesselName || 'VESSEL';
    const voyage = data?.voyage || 'VOY';
    const type = data?.type || 'PDA';

    const port = data?.port || 'PORT';
    const purpose = data?.purpose || 'PURPOSE';
    const portAgent = data?.portAgent || 'AGENCY';
    const pdaAmount = data?.pdaAmount || '0';
    const advancePay = data?.advancePay || '0';
    const advance_percentage = data?.advancePercentage;
    const due = this.formatDate(data?.due);
    const disbursement_id = this.data.disbursement_id;
    const currency = this.data.pmt_curr_to?.toUpperCase() || '';
    this.subject = `${type} - CONFIRMATION - ${vesselName} - VOY - ${voyage} - ${purpose} - ${port} - DISBURSEMENT ID - ${disbursement_id}`;

    this.greetingText = `Good Day,\n\nKindly consider below details for subject vessel ${type} approval.`;

    this.tableData = [
      { label: 'Vessel - Voyage', value: `${vesselName} - VOY - ${voyage}` },
      {
        label: 'Vessel IMO',
        value: this.data?.vessel_imo || 'N/A',
      },
      { label: 'Port - Call Purpose', value: `${port} - ${purpose}` },
      { label: 'Agency', value: portAgent },
      { label: `${type} Amount`, value: `${currency} ${pdaAmount}` },
    ];

    if (
      advance_percentage !== undefined &&
      advance_percentage !== null &&
      advance_percentage > 0
    ) {
      this.tableData.push({
        label:
          this.data?.type === 'PDA'
            ? `${advance_percentage}% Advance Pay`
            : 'Advance Pay',
        value: `${currency} ${advancePay}`,
      });
    }
    const pda = Number(pdaAmount);
    const fda = Number(pdaAmount);
    const advance = Number(advancePay);
    const balanceAmount =
      this.data?.type === 'PDA' ? pda - advance : fda - advance;
    if (this.data?.type === 'FDA') {
      this.tableData.push({
        label: 'Balance Amount',
        value: `${currency} ${balanceAmount}`,
      });
    }
    this.tableData.push({ label: 'Due', value: due });
    // Directly assign office address without any restrictions
    this.portAgentOfficeAddress =
      data?.port_agent_office_address ||
      data?.portAgentOfficeAddress ||
      (data as any)?.office_address ||
      (data as any)?.officeAddress ||
      null;

    // Map bank details
    const bankDetails = data?.bankDetails;
    if (bankDetails) {
      this.bankDetailsData = {
        account_holder_name: bankDetails.account_holder_name || bankDetails.beneficiary_acc_holder_name || '',
        bank_name: bankDetails.bank_name || '',
        branch_address: bankDetails.branch_address || '',
        account_no: bankDetails.account_no || '',
        iban_number: bankDetails.iban_number || '',
        correspondent_account_number: bankDetails.correspondent_account_number || '',
        swift_code: bankDetails.swift_code || '',
        ifsc_code: bankDetails.ifsc_code || '',
        bik_code: bankDetails.bik_code || bankDetails.bic_code || '',
        inn_code: bankDetails.inn_code || '',
      };
    }
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    return new Date(date)
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .toUpperCase();
  }

  ngOnInit() {
    this.filteredToOptions = this.to.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterOptions(value || '', this.toEmailList)),
    );

    this.filteredCcOptions = this.cc.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterOptions(value || '', this.ccEmailList)),
    );
    // this.addExistingPdfAttachment();
    this.fetchDisbursementDocuments();
    this.getReportPdf();
  }
 dbAttachments: { name: string; url: string }[] = [];
isLoadingAttachments = false;

  fetchDisbursementDocuments(): void {
    const type = this.data?.type?.toUpperCase();
  if (!this.data?.disbursement_seq) return;

  const requestData = {
    disbursement_seq: this.data.disbursement_seq,
    pda_fda_flag: type || 'DIS',
  };

  this.isLoadingAttachments = true;

  this.fileUploadService.fileDataFromDb(requestData).subscribe({
    next: (files: any[]) => {
      this.isLoadingAttachments = false;
      if (!Array.isArray(files)) return;

      files.forEach((file) => {
        if (file.presigned_url) {
          // Use 'name' from the API response
          const fileName = file.name || file.file_name || 'Document.pdf';

          // 1. Store for UI rendering
          this.dbAttachments.push({
            name: fileName,
            url: file.presigned_url,
          });

          // 2. Fetch blob for email attachment
          fetch(file.presigned_url)
            .then((res) => res.blob())
            .then((blob) => {
              this.attachments.push({
                file: blob,
                name: fileName,
              });
            })
            .catch((err) => console.error(`Failed to fetch blob for ${fileName}`, err));
        }
      });
    },
    error: (err) => {
      this.isLoadingAttachments = false;
      console.error('Failed to fetch disbursement files from DB', err);
    },
  });
}

  private filterOptions(value: string, datasource: string[]): string[] {
    const filterValue = value.toLowerCase();
    return datasource.filter((option) => {
      if (typeof option === 'string') {
        return option.toLowerCase().includes(filterValue);
      }
      return false;
    });
  }

  private addEmailToSelection(email: string, targetArray: string[]): boolean {
    if (!email || targetArray.includes(email)) {
      return false;
    }
    targetArray.push(email);
    return true;
  }
  addToEmail(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (this.addEmailToSelection(value, this.selectedToEmails)) {
      event.chipInput?.clear();
      this.to.setValue('');
    }
  }

  selectToEmail(event: any): void {
    const value = event.option?.viewValue;
    if (this.addEmailToSelection(value, this.selectedToEmails)) {
      this.to.setValue('');
    }
  }

  removeToEmail(email: string): void {
    this.selectedToEmails = this.selectedToEmails.filter((e) => e !== email);
  }

  addCcEmail(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (this.addEmailToSelection(value, this.selectedCcEmails)) {
      event.chipInput?.clear();
      this.cc.setValue('');
    }
  }

  selectCcEmail(event: any): void {
    const value = event.option?.viewValue;
    if (this.addEmailToSelection(value, this.selectedCcEmails)) {
      this.cc.setValue('');
    }
  }

  removeCcEmail(email: string): void {
    this.selectedCcEmails = this.selectedCcEmails.filter((e) => e !== email);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  updateTableValue(row: any, event: any): void {
    row.value = event.target.innerText;
  }

  updateOfficeAddress(event: Event): void {
    const target = event.target as HTMLElement;
    this.portAgentOfficeAddress = target.innerText.trim();
  }

  updateBankDetail(
    field:
      | 'account_holder_name'
      | 'account_no'
      | 'swift_code'
      | 'bank_name'
      | 'branch_address'
      | 'correspondent_account_number'
      | 'ifsc_code'
      | 'bik_code'
      | 'bic_code'
      | 'inn_code'
      | 'iban_number',
    event: Event,
  ): void {
    if (!this.bankDetailsData) {
      return;
    }

    const target = event.target as HTMLElement;
    this.bankDetailsData[field] = target.innerText.trim();
  }

  onContinue(): void {
    if (this.selectedToEmails.length === 0) {
      return;
    }
    if (this.update_signature.value) {
      sessionStorage.setItem('signature', this.signature.value ?? '');
    }

    const topText =
      this.topTextDiv?.nativeElement?.innerText || this.greetingText;
    const bottomText =
      this.bottomTextDiv?.nativeElement?.innerHTML ||
      '<b>Charterers account items / World scale items</b> - No such cost in invoice<br><b>Owners account items rejected</b> - No owner cost item in invoice.';

    const tableObject = this.tableData.reduce((acc, row) => {
      acc[row.label] = row.value;
      return acc;
    }, {} as any);

    this.dialogRef.close({
      email_to: this.selectedToEmails,
      email_cc: this.selectedCcEmails,
      subject: this.subject,

      body: {
        upper_text: topText,
        table: tableObject,
        lower_text: bottomText,
      },

      signature: this.signature.value ?? '',
      update_signature: this.update_signature.value ? 'Y' : 'N',

      port_agent_office_address: this.portAgentOfficeAddress,
      bank_details: this.bankDetailsData || this.data?.bankDetails || null,

      attachments: this.attachments,
    });
  }

  attachments: {
    file: Blob;
    name: string;
  }[] = [];

  getReportPdf(): void {
    const type = this.data?.type?.toUpperCase();

    if (type !== 'PDA' && type !== 'FDA') {
      return;
    }

    if (!this.data?.disbursement_id) {
      return;
    }

    const requestData = {
      disbursement_seq: this.data.disbursement_seq,
      report_type: type,
    };

    this.isReportLoading = true;
    this.fileUploadService
      .viewReport(requestData, this.data?.disbursement_id)
      .subscribe({
        next: (pdfBlob: Blob) => {
          this.reportPdfUrl = URL.createObjectURL(
            new Blob([pdfBlob], { type: 'application/pdf' }),
          );

          this.reportPdfName = `${this.data.disbursement_id}_${type}.pdf`;
          const pdfFile = new Blob([pdfBlob], { type: 'application/pdf' });

          this.attachments.push({
            file: pdfFile,
            name: `${this.data.disbursement_id}_${type}.pdf`,
          });
          this.isReportLoading = false;
        },

        error: (error) => {
          console.error(`Failed to generate ${type} PDF`, error);
        },
      });
  }

  addExistingPdfAttachment(): void {
    if (!this.data?.presignedUrl) {
      return;
    }

    fetch(this.data.presignedUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to download existing PDF');
        }

        return response.blob();
      })
      .then((blob) => {
        this.attachments.push({
          file: blob,
          name: this.data.pdf_name || 'Existing.pdf',
        });
      })
      .catch((error) => {
        console.error('Failed to add existing PDF:', error);
      });
  }
}

interface AttachmentItem {
  file?: Blob;
  name: string;
  url: string;
  loading?: boolean;
}